import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, Paper, Grid } from '@mui/material';
import { Coffee, Favorite, LocationOn, Chat, Security, Android, Apple } from '@mui/icons-material';

// Facebook Pixel declaration
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

type DeviceType = 'ios' | 'android' | 'other';

const detectDevice = (): DeviceType => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }

  if (/android/i.test(userAgent)) {
    return 'android';
  }

  return 'other';
};

const AppPage: React.FC = () => {
  const appStoreUrl = 'https://apps.apple.com/tr/app/sobcof/id6751366978?l=tr';
  const marketUrl = 'market://details?id=com.sobcof.sobcofandroid';
  const playStoreWebUrl = 'https://play.google.com/store/apps/details?id=com.sobcof.sobcofandroid';

  const [deviceType, setDeviceType] = useState<DeviceType>('other');

  useEffect(() => {
    const device = detectDevice();
    setDeviceType(device);

    if (device === 'ios') {
      window.location.href = appStoreUrl;
    } else if (device === 'android') {
      window.location.href = marketUrl;
      setTimeout(() => {
        window.location.href = playStoreWebUrl;
      }, 2000);
    }
  }, []);

  const handleDownloadClick = (store: 'ios' | 'android') => {
    // Facebook Pixel - StartTrial event for app download
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'StartTrial', {
        content_name: 'App Download',
        content_category: store === 'ios' ? 'App Store' : 'Google Play'
      });
    }

    if (store === 'ios') {
      window.open(appStoreUrl, '_blank');
    } else if (store === 'android') {
      window.location.href = marketUrl;
      setTimeout(() => {
        window.location.href = playStoreWebUrl;
      }, 1000);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2b1d16 0%, #4a2c1d 100%)',
      py: 4,
      fontFamily: '"Outfit", "Inter", sans-serif'
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 8 },
          textAlign: 'center',
          borderRadius: 8,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF'
        }}>
          <Box sx={{ maxWidth: 800, mx: 'auto', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box component="img"
                src="/assets/LogoWhite.png"
                alt="SobCof Logo"
                sx={{
                  width: 300,
                  height: 'auto',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
                }}
              />
            </Box>

            <Typography variant="h5" sx={{
              color: '#8B4513',
              fontWeight: 800, // Bolder
              mb: 4,
              letterSpacing: '2px', // More spacing for premium feel
              textTransform: 'uppercase'
            }}>
              KAFE ODAKLI SOSYAL PLATFORM
            </Typography>

            <Typography variant="h6" sx={{
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.8,
              color: '#FFFFFF',
              opacity: 0.9,
              fontWeight: 400
            }}>
              Kahve tutkunu arkadaşlar edin! Yakındaki kafeleri keşfet, benzer zevklere sahip kişilerle sosyalleş.
            </Typography>

            {/* Device-Specific Status */}
            <Box sx={{ mb: 6 }}>
              {deviceType !== 'other' && (
                <Typography variant="body1" sx={{ color: '#8B4513', fontWeight: 600, mb: 2 }}>
                  Uygulama mağazasına yönlendiriliyorsunuz...
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Apple />}
                  onClick={() => handleDownloadClick('ios')}
                  sx={{
                    background: '#FFFFFF',
                    color: '#000000',
                    '&:hover': {
                      background: '#f0f0f0',
                      transform: 'translateY(-1px)',
                    },
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 4,
                    textTransform: 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  App Store
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Android />}
                  onClick={() => handleDownloadClick('android')}
                  sx={{
                    background: '#8B4513',
                    color: '#FFFFFF',
                    '&:hover': {
                      background: '#6d3610',
                      transform: 'translateY(-1px)',
                    },
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 4,
                    textTransform: 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Google Play
                </Button>
              </Box>
            </Box>
          </Box>

        </Paper>

        {/* Features Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {[
            { icon: <Security />, title: "Güvenli Ortam", desc: "Anonim sohbet ile gizliliğini koru." },
            { icon: <Coffee />, title: "Kahve Kültürü", desc: "Güvenli ve elit kahve severler topluluğu." },
            { icon: <LocationOn />, title: "Yakındaki Kafeler", desc: "Sana en yakın ve en iyi mekanları keşfet." },
            { icon: <Favorite />, title: "Gerçek Dostluklar", desc: "Benzer zevklere sahip kişilerle tanış." },
            { icon: <Chat />, title: "Modern Tasarım", desc: "Hızlı, dinamik ve kullanıcı dostu deneyim." }
          ].map((feature, idx) => (
            <Grid key={idx} size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Box sx={{
                p: 3,
                textAlign: 'center',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: 4,
                border: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.05)',
                  transform: 'translateY(-4px)'
                }
              }}>
                <Box sx={{ color: '#8B4513', mb: 2 }}>
                  {React.cloneElement(feature.icon as React.ReactElement<any>, { sx: { fontSize: 32 } })}
                </Box>
                <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.6 }}>
                  {feature.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 4, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 500 }}>
            © 2025 SobCof. Tüm hakları saklıdır.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AppPage;

